<template>
    <div class="textarea-editor">
        <editor-content :editor="editor" />
        <div class="textarea-editor-options">
            <div class="left-options">
                <div class="container-icon">
                    <i class="fa-regular fa-face-laugh" @click="displayEmojiPicker = !displayEmojiPicker"></i>
                    <Picker
                    :data="emojiIndex"
                    v-show="displayEmojiPicker"
                    :style="pickerStyle"
                    @select="addEmoji"
                    ref="emojiPicker"
                    :i18n="{
                        search: 'Rechercher un Emoji',
                        notfound: 'Aucun emoji trouvé',
                        categories: {
                            search: 'Résultats de recherche',
                            recent: 'Utilisés fréquemment',
                            smileys: 'Émoticônes & Émotion',
                            people: 'Personnes & Corps',
                            nature: 'Animaux & Nature',
                            foods: 'Nourriture & Boissons',
                            activity: 'Activité',
                            places: 'Voyages & Lieux',
                            objects: 'Objets',
                            symbols: 'Symboles',
                            flags: 'Drapeaux',
                            custom: 'Personnalisé',
                        },
                    }"
                    :color="'#b64985'"
                    />
                </div>
                <div v-if="showOptionBold" class="container-icon" :class="{ disable: !hasSelection}" @click="onBoldButton()">
                    <i class="fa-solid fa-bold"></i>
                </div>
                <div v-if="showOptionItalic" class="container-icon" :class="{ disable: !hasSelection }" @click="onItalicButton()">
                    <i class="fa-solid fa-italic"></i>
                </div>
                <div class="container-icon" :class="{disable : disableHashtag}" @mousedown.prevent="onHashtagButton">
                    <i class="fa-solid fa-hashtag"></i>
                    <p v-if="!compactOption">Hashtag</p>
                </div>
                <div class="container-icon" :class="{disable : disableMention}" @click="onMentionButton">
                    <i class="fa-solid fa-at"></i>
                    <p v-if="!compactOption">Mention</p>
                </div>
            </div>
            <Tooltip
                v-if="ShowCounterCharacters"
                position="bottom"
                classCustom="right-options"
                :active="textInfoMaxCharacters.length"
                :text="textInfoMaxCharacters"
            >
            <i v-if="maxCharacters !== Infinity && charactersLength > maxCharacters" class="fa-solid fa-circle-exclamation"></i>
            <p class="count" >
                <span :class="{red : maxCharacters !== Infinity && charactersLength > maxCharacters}">{{ charactersLength }}</span>
                <template v-if="maxCharacters !== Infinity">
                    /<span>{{ maxCharacters }}</span>
                </template>
            </p>
        </Tooltip>
    </div>
</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import emojiData from '@/constants/emoji-mart-vue-fast/all.json'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import minxinNotification from '@/components/mixins/Notif.vue'

export default {
    name: 'MentionEditor',

    mixins: [minxinNotification],
    
    components: { EditorContent, Picker, Tooltip },
    
    props: {
        users: {
            type: Array,
            default: () => [],
        },
        pickerStyle: {
            type: String,
            default: '',
        },
        text: {
            type: String,
            default: '<p></p>',
        },
        showOptionBold: {
            type: Boolean,
            default: true
        },
        showOptionItalic: {
            type: Boolean,
            default: true
        },
        ShowCounterCharacters: {
            type: Boolean,
            default: true
        },
        compactOption: {
            type: Boolean,
            default: false
        },
        disableHashtag: Boolean,
        disableMention: Boolean,
        charactersLength: {
            type: Number,
            default: 0
        },
        maxCharacters: {
            type: Number,
            default: Infinity
        },
        textInfoMaxCharacters: {
            type: String,
            default: ''
        },
    },
    
    data() {
        return {
            editor: null,
            displayEmojiPicker: false,
            emojiIndex: new EmojiIndex(emojiData, {
                recentLength: 18,
                recent: ['tada','sparkles','rocket','confetti_ball','fire','heart','speech_balloon','heart_eyes','raised_hands','two_hearts','bulb','brain','loudspeaker','star2','wrench','hammer_and_wrench','calendar','spiral_calendar_pad'],
            }),
        }
    },
    
    mounted() {
        const self = this
        
        this.editor = new Editor({
            onUpdate: ({ editor }) => {
                this.$emit('change', editor.getHTML()) // ou getJSON() ou getText()
                
            },
            onBlur: ({ editor, event }) => {
                this.$emit('onBlur', event)
            },
            extensions: [
                StarterKit,
                Mention.configure({
                    renderHTML({ node }) {
                        return ['span', { class: 'tag-mention', 'data-id': node.attrs.id }, `@${node.attrs.label}`]
                    },
                    suggestion: {
                        char: '@',
                        allow: (e) => {
                            console.log('allow mention', window.event?.type, window.event, window.event?.type)
                            if (!window.event) return true //windows event indisponible
                            if (window.event?.type === 'keypress' || window.event?.type === 'input' && (window?.event?.key === '@' || window?.event?.data === '@' )) return true
                            if (window.event?.type === 'click') return true
                            return false
                        },
                        items: ({ query }) => {
                            return self.users.filter(u =>
                            u.name.toLowerCase().includes(query.toLowerCase())
                            )
                        },
                        render: () => ({
                            onStart(props) {
                                self.$emit('newMention', {
                                    resolve: (mention) => {
                                        props.command({ id: mention.id, label: mention.text })
                                        
                                    },
                                })
                            },
                            onUpdate() {},
                            onExit() {},
                            onKeyDown() { return false },
                        }),
                    },
                }).extend({
                    parseHTML() {
                        return [
                        {
                            tag: 'span.tag-mention',
                            getAttrs: el => ({
                                id: el.getAttribute('data-id'),
                                label: el.textContent.replace('@', '').trim(),
                            }),
                        },
                        ]
                    },
                    addNodeView() {
                        return ({ node, getPos, editor }) => {
                            //pour editer les mentions au click
                            let isEditing = false
                            
                            const dom = document.createElement('span')
                            dom.className = 'tag-mention'
                            dom.setAttribute('data-id', node.attrs.id)
                            
                            const at = document.createElement('span')
                            at.textContent = '@'
                            at.contentEditable = false
                            
                            const label = document.createElement('span')
                            label.className = 'tag-mention-label'
                            label.contentEditable = false
                            label.textContent = node.attrs.label




                            //editon du label de la mention
                            let clickTimer = null

                            label.addEventListener('click', (e) => {
                                if (isEditing) return
                                console.log('click mention', { id: node.attrs.id, label: node.attrs.label })
                                
                                label.contentEditable = true
                                
                                clickTimer = setTimeout(() => {
                                    clickTimer = null
                                    label.contentEditable = false
                                    self.clickMention({ 
                                        id: node.attrs.id, 
                                        text: node.attrs.label 
                                    })
                                }, 200)
                                
                                e.stopPropagation()
                                
                            })
                            
                            label.addEventListener('focus', (e) => { 
                                console.log('focus mention')
                                
                                if (clickTimer) {
                                    clearTimeout(clickTimer)
                                    clickTimer = null
                                }
                                
                                // Place le curseur à la fin du label
                                const range = document.createRange()
                                const sel = window.getSelection()
                                range.selectNodeContents(label)
                                range.collapse(false) // false = fin
                                sel.removeAllRanges()
                                sel.addRange(range)
                                
                                isEditing = true
                                
                                
                            })
                            
                            label.addEventListener('blur', () => {
                                isEditing = false
                                label.contentEditable = false
                                // On dispatch uniquement au blur pour éviter les re-renders pendant la frappe
                                const { tr } = editor.state
                                tr.setNodeMarkup(getPos(), null, {
                                    ...node.attrs,
                                    label: label.textContent,
                                })
                                editor.view.dispatch(tr)
                                
                                
                                
                                self.$emit('updateMentionLabel', {
                                    id: node.attrs.id,
                                    text: label.textContent,
                                })
                            })
                            
                            label.addEventListener('input', () => {
                                self.$emit('updateMentionLabel', {
                                    id: node.attrs.id,
                                    text: label.textContent,
                                })
                            })

                            //ajout d'un span
                            const after = document.createElement('span')
                            after.className = 'tag-mention-after'

                            after.addEventListener('click', () => {
                                console.log('click after mention')
                                // Place le curseur de Tiptap après la mention
                                const pos = getPos() + node.nodeSize
                                editor.chain().focus().setTextSelection(pos).run()
                            })
                                                        
                            
                            dom.appendChild(at)
                            dom.appendChild(label)
                            dom.appendChild(after)

                            return {
                                dom,
                                contentDOM: null,
                                update(updatedNode) {
                                    // Si on est en train d'éditer, on ne laisse pas Tiptap écraser le DOM
                                    if (isEditing) return true
                                    if (updatedNode.type.name !== 'mention') return false
                                    label.textContent = updatedNode.attrs.label
                                    dom.setAttribute('data-id', updatedNode.attrs.id)
                                    return true
                                },
                            }
                            
                        }
                    },
                }),
            ],
        })


        //permet de placer son cursor après une mention même si il y a un retour à la ligne juste après
        this.editor.view.dom.addEventListener('click', (e) => {
            if (e.target.tagName !== 'P') return

            const p = e.target
            const lastMention = [...p.querySelectorAll('.tag-mention')].pop()
            const lastBr = p.lastElementChild?.tagName === 'BR' ? p.lastElementChild : null

            if (lastMention && lastBr) {
                // Vérifie si le clic est après la mention via la position X
                const mentionRect = lastMention.getBoundingClientRect()
                if (e.clientX > mentionRect.right) {
                const pos = this.editor.view.posAtDOM(lastMention, 0) + 1
                this.editor.chain().focus().setTextSelection(pos).run()
                }
            }
        })

        console.log('update content', this.text)
        this.setContent(this.text) 
        console.log('this.editor.getJSON()',this.editor.getJSON())
    },
    
    beforeDestroy() {
        this.editor.destroy()
    },


    computed: {
        hasSelection() {
            if (!this.editor) return false
            return !this.editor.state.selection.empty
        }
    },
    
    methods: {
        addEmoji(emoji) {
            this.editor.chain().focus().insertContent(emoji.native).run()
            this.displayEmojiPicker = false
        },
        
        onMentionButton() {
            if (this.disableMention) return
            console.log('onMentionButton')
            const { state } = this.editor
            const { from, to, empty } = state.selection
            
            if (!empty) {
                const selectedText = state.doc.textBetween(from, to)
                this.$emit('newMention', {
                    text: selectedText,
                    resolve: (mention) => {
                        this.editor.chain().focus()
                        .deleteSelection()
                        .insertContent({
                            type: 'mention',
                            attrs: { id: mention.id, label: mention.text },
                        })
                        .run()
                    },
                })
            } else {
                this.editor.chain().focus().insertContent('@').run()
            }
        },
        
        onHashtagButton() {
            console.log('onHashtagButton', this.disableHashtag) 
            if (this.disableHashtag) return
            const isFocused = this.editor.isFocused
            const docSize = this.editor.state.doc.content.size
            const { from } = this.editor.state.selection
            
            const insertPos = isFocused ? from : docSize - 1
            
            this.$emit('newHashtag', {
                resolve: (hashtags) => {
                    if (!hashtags || !hashtags.length) return
                    const content = hashtags.map(tag => `#${tag}`).join(' ') + ' '
                    this.editor.chain()
                    .focus()
                    .insertContentAt(insertPos, content)
                    .run()
                },
            })
        },
        
        onItalicButton() {
            const { state } = this.editor
            const { from, to } = state.selection
            const selectedText = state.doc.textBetween(from, to)
            const hasAccent = /[àâäéèêëîïôöùûüçœæÀÂÄÉÈÊËÎÏÔÖÙÛÜÇŒÆ]/.test(selectedText)
            const isAlreadyItalic = this.editor.isActive('italic')

            if (hasAccent && !isAlreadyItalic) {
                this.notify({
                    title: 'Mise en italique avec accents',
                    text: 'Le texte en italique contient des accents qui peuvent être mal interprétés sur certains réseaux sociaux',
                    color: 'primary',
                    time: 0,
                    id: Date.now()
                })
            }

            this.editor.chain().focus().toggleItalic().run()
        },

        onBoldButton() {
            const { state } = this.editor
            const { from, to } = state.selection
            const selectedText = state.doc.textBetween(from, to)
            const hasAccent = /[àâäéèêëîïôöùûüçœæÀÂÄÉÈÊËÎÏÔÖÙÛÜÇŒÆ]/.test(selectedText)
            const isAlreadyBold = this.editor.isActive('bold')

            if (hasAccent && !isAlreadyBold) {
                this.notify({
                    title: 'Mise en gras avec accents',
                    text: 'Le texte en gras contient des accents qui peuvent être mal interprétés sur certains réseaux sociaux',
                    color: 'primary',
                    time: 0,
                    id: Date.now()
                })
            }

            this.editor.chain().focus().toggleBold().run()
        },

        clickMention({id, text}) {
            
            this.$emit('editMention', {
                id,
                text,
                resolve: (mention) => {
                    let isFound = false
                    
                    this.editor.state.doc.descendants((node, pos) => {
                        if (isFound || node.type.name !== 'mention' || parseInt(node.attrs.id) !== parseInt(id)) return
                        
                        if (mention && mention.id) {
                            const { tr } = this.editor.state
                            tr.setNodeMarkup(pos, null, { id: mention.id, label: mention.text })
                            this.editor.view.dispatch(tr)
                        } else {
                            this.editor.chain()
                            .setNodeSelection(pos)
                            .insertContent(text)
                            .run()
                        }
                        
                        isFound = true
                    })
                },
            })
        },

        setContent(content) {
            console.log('setContent', content)
            this.editor.commands.setContent(content)
        },
    },
}
</script>